import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import toast from 'react-hot-toast'
import { PhoneIcon, ClockIcon, ShieldCheckIcon, ArrowRightIcon, ExclamationCircleIcon, ArrowLeftIcon } from '@heroicons/react/24/outline'

const PhoneLoginPage = () => {
  const [formData, setFormData] = useState({
    phone: '',
    code: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [codeSent, setCodeSent] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [focusedField, setFocusedField] = useState<string>('')
  const [isCodeLoading, setIsCodeLoading] = useState(false)
  
  const navigate = useNavigate()
  const setUser = useAuthStore(state => state.setUser)
  const setTokens = useAuthStore(state => state.setTokens)

  // 倒计时效果
  useEffect(() => {
    let timer: NodeJS.Timeout
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000)
    }
    return () => clearTimeout(timer)
  }, [countdown])

  const validatePhone = (phone: string) => {
    if (!phone.trim()) {
      return '请输入手机号'
    }
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      return '请输入有效的手机号码'
    }
    return ''
  }

  const validateCode = (code: string) => {
    if (!code.trim()) {
      return '请输入验证码'
    }
    if (!/^\d{4,6}$/.test(code)) {
      return '验证码应为4-6位数字'
    }
    return ''
  }

  const sendVerificationCode = async () => {
    const phoneError = validatePhone(formData.phone)
    if (phoneError) {
      setErrors({ phone: phoneError })
      toast.error(phoneError)
      return
    }

    setIsCodeLoading(true)
    try {
      const response = await fetch('/api/auth/send-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          identifier: formData.phone,
          type: 'login'
        })
      })

      const data = await response.json()

      if (response.ok) {
        setCodeSent(true)
        setCountdown(60)
        setErrors({})
        toast.success('验证码已发送到您的手机', {
          icon: '📱',
          duration: 3000,
        })
      } else {
        toast.error(data.message || '发送失败，请稍后重试')
        setErrors({ phone: data.message || '发送失败' })
      }
    } catch (error) {
      toast.error('网络错误，请检查网络连接')
    } finally {
      setIsCodeLoading(false)
    }
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()

    const phoneError = validatePhone(formData.phone)
    const codeError = validateCode(formData.code)

    if (phoneError || codeError) {
      setErrors({
        phone: phoneError,
        code: codeError
      })
      toast.error('请检查输入信息')
      return
    }

    setIsLoading(true)
    try {
      const response = await fetch('/api/auth/login/phone', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      const data = await response.json()

      if (response.ok) {
        setUser(data.user)
        setTokens(data.token, data.refreshToken || data.token)
        localStorage.setItem('token', data.token)
        
        toast.success('登录成功！正在跳转...', {
          icon: '🎉',
          duration: 2000,
        })
        
        // 根据用户角色跳转
        setTimeout(() => {
          if (data.user.role === 'admin') {
            navigate('/admin/dashboard')
          } else if (data.user.role === 'teacher') {
            navigate('/teacher/dashboard')
          } else {
            navigate('/student/dashboard')
          }
        }, 1000)
      } else {
        toast.error(data.message || '登录失败，请检查验证码')
        setErrors({ code: data.message || '验证码错误' })
      }
    } catch (error) {
      toast.error('网络连接失败，请稍后重试')
    } finally {
      setIsLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    
    let processedValue = value
    if (name === 'phone') {
      // 只允许数字
      processedValue = value.replace(/\D/g, '')
      // 限制11位
      if (processedValue.length > 11) {
        processedValue = processedValue.substring(0, 11)
      }
    } else if (name === 'code') {
      // 只允许数字
      processedValue = value.replace(/\D/g, '')
      // 限制6位
      if (processedValue.length > 6) {
        processedValue = processedValue.substring(0, 6)
      }
    }
    
    setFormData({
      ...formData,
      [name]: processedValue
    })
    
    // 清除对应字段的错误
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      })
    }
  }

  const handleFocus = (fieldName: string) => {
    setFocusedField(fieldName)
  }

  const handleBlur = () => {
    setFocusedField('')
  }

  const formatPhoneDisplay = (phone: string) => {
    if (phone.length > 7) {
      return `${phone.substring(0, 3)} ${phone.substring(3, 7)} ${phone.substring(7)}`
    } else if (phone.length > 3) {
      return `${phone.substring(0, 3)} ${phone.substring(3)}`
    }
    return phone
  }

  const phoneHighlights = [
    { title: '极速验证', desc: '平均 8 秒完成验证码发送与校验' },
    { title: '风险拦截', desc: '智能识别异常登录，实时通知' },
    { title: '多端同步', desc: '手机验证码登录后自动同步所有设备' },
  ]

  const verificationSteps = [
    { label: '输入手机号', desc: '支持大陆手机号，自动校验格式' },
    { label: '获取验证码', desc: '60 秒倒计时，短信和站内同时推送' },
    { label: '完成登录', desc: '一键跳转到专属角色看板' },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-cyan-50 py-12 px-4 sm:px-6 lg:px-10 relative overflow-hidden">
      {/* 返回首页按钮 */}
      <Link
        to="/"
        className="fixed top-6 left-6 z-20 flex items-center space-x-2 bg-white/80 backdrop-blur-lg rounded-xl px-4 py-2 shadow-lg hover:shadow-xl transition-all duration-300 hover:bg-white/90 text-gray-700 hover:text-blue-600"
      >
        <ArrowLeftIcon className="h-5 w-5" />
        <span className="font-medium">返回首页</span>
      </Link>

      {/* 背景装饰 */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-green-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-center">
        {/* 左侧亮点 */}
        <div className="hidden lg:flex flex-col justify-between text-gray-900">
          <div className="bg-white/85 backdrop-blur-2xl rounded-3xl border border-white/50 shadow-2xl p-10">
            <p className="uppercase tracking-[0.4em] text-emerald-400 text-xs mb-4">secure login</p>
            <h2 className="text-4xl font-bold leading-tight mb-6">手机号快捷登录，随时接入你的学习空间</h2>
            <p className="text-gray-600">
              无需记忆密码，验证码自动匹配角色身份，审核、课表、任务一站通行。
            </p>
            <div className="mt-10 space-y-5">
              {phoneHighlights.map(item => (
                <div key={item.title} className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-500 flex items-center justify-center font-semibold">
                    ✓
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-gray-900">{item.title}</p>
                    <p className="text-gray-500 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 border-t border-white/40 pt-6 text-center">
              {[
                { label: '验证码成功率', value: '99.3%' },
                { label: '平均响应', value: '2.4s' },
                { label: '安全告警', value: '实时' }
              ].map(stat => (
                <div key={stat.label}>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  <p className="text-xs text-gray-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 bg-white/80 border border-white/50 rounded-3xl p-6 backdrop-blur-xl shadow-xl">
            <p className="text-sm font-semibold text-gray-700 mb-4">流程说明</p>
            <div className="space-y-4">
              {verificationSteps.map((step, index) => (
                <div key={step.label} className="flex items-start space-x-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center font-semibold">
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{step.label}</p>
                    <p className="text-sm text-gray-500">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="relative z-10 max-w-md w-full mx-auto lg:mx-0">
        {/* 登录卡片 */}
        <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/30 p-8 animate-fadeInUp">
          <div className="text-center mb-8">
            {/* Logo区域 */}
            <div className="mx-auto w-16 h-16 bg-gradient-to-r from-green-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg animate-pulse-subtle">
              <PhoneIcon className="h-8 w-8 text-white" />
            </div>
            
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              手机号登录
            </h2>
            <p className="text-gray-600">
              快速安全的验证码登录方式
            </p>
          </div>

          {/* 快捷登录方式 */}
          <div className="mb-6">
            <div className="flex items-center space-x-4 text-sm">
              <Link 
                to="/login" 
                className="flex-1 text-center py-2 px-4 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg transition-all duration-200 hover:scale-105 transform"
              >
                ✉️ 邮箱登录
              </Link>
              <Link 
                to="/register" 
                className="flex-1 text-center py-2 px-4 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-all duration-200 hover:scale-105 transform"
              >
                🆕 立即注册
              </Link>
            </div>
          </div>

          {/* 分割线 */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">手机验证码登录</span>
            </div>
          </div>
          
          <form className="space-y-6" onSubmit={handleLogin}>
            {/* 手机号输入框 */}
            <div className="space-y-1">
              <label htmlFor="phone" className="text-sm font-medium text-gray-700 block">
                手机号码
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 text-sm">+86</span>
                </div>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  required
                  className={`block w-full pl-12 pr-3 py-3 border rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 ${
                    errors.phone 
                      ? 'border-red-300 bg-red-50' 
                      : focusedField === 'phone'
                        ? 'border-green-300 bg-green-50/50'
                        : 'border-gray-300 bg-gray-50/50 hover:bg-white'
                  }`}
                  placeholder="请输入您的手机号"
                  value={formData.phone}
                  onChange={handleChange}
                  onFocus={() => handleFocus('phone')}
                  onBlur={handleBlur}
                  maxLength={11}
                />
                {formData.phone && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <span className="text-xs text-gray-400">
                      {formData.phone.length}/11
                    </span>
                  </div>
                )}
              </div>
              {errors.phone && (
                <p className="text-sm text-red-600 animate-slideUp flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.phone}
                </p>
              )}
              {formData.phone && !errors.phone && (
                <p className="text-sm text-green-600 animate-slideUp">
                  手机号：{formatPhoneDisplay(formData.phone)}
                </p>
              )}
            </div>

            {/* 验证码输入框 */}
            <div className="space-y-1">
              <label htmlFor="code" className="text-sm font-medium text-gray-700 block">
                验证码
              </label>
              <div className="flex space-x-3">
                <div className="flex-1 relative">
                  <input
                    id="code"
                    name="code"
                    type="text"
                    autoComplete="one-time-code"
                    required
                    className={`block w-full px-3 py-3 border rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 ${
                      errors.code 
                        ? 'border-red-300 bg-red-50' 
                        : focusedField === 'code'
                          ? 'border-green-300 bg-green-50/50'
                          : 'border-gray-300 bg-gray-50/50 hover:bg-white'
                    }`}
                    placeholder="输入验证码"
                    value={formData.code}
                    onChange={handleChange}
                    onFocus={() => handleFocus('code')}
                    onBlur={handleBlur}
                    maxLength={6}
                    disabled={!codeSent}
                  />
                  {formData.code && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <span className="text-xs text-gray-400">
                        {formData.code.length}/6
                      </span>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={sendVerificationCode}
                  disabled={isCodeLoading || countdown > 0 || !formData.phone || !!errors.phone}
                  className={`px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 whitespace-nowrap ${
                    isCodeLoading || countdown > 0 || !formData.phone || !!errors.phone
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-green-500 hover:bg-green-600 text-white transform hover:scale-105 shadow-md hover:shadow-lg'
                  }`}
                >
                  {isCodeLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                      发送中
                    </div>
                  ) : countdown > 0 ? (
                    <div className="flex items-center">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      {countdown}s
                    </div>
                  ) : codeSent ? (
                    '重新发送'
                  ) : (
                    '获取验证码'
                  )}
                </button>
              </div>
              {errors.code && (
                <p className="text-sm text-red-600 animate-slideUp flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.code}
                </p>
              )}
              {codeSent && !errors.code && (
                <p className="text-sm text-green-600 animate-slideUp flex items-center">
                  <ShieldCheckIcon className="h-4 w-4 mr-1" />
                  验证码已发送至 {formatPhoneDisplay(formData.phone)}
                </p>
              )}
            </div>

            {/* 登录按钮 */}
            <button
              type="submit"
              disabled={isLoading || !codeSent || !formData.code}
              className={`group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-xl text-white transition-all duration-200 ${
                isLoading || !codeSent || !formData.code
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transform hover:scale-105 shadow-lg hover:shadow-xl'
              }`}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  登录中...
                </div>
              ) : (
                <div className="flex items-center">
                  登录
                  <ArrowRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                </div>
              )}
            </button>
          </form>

          {/* 安全提示 */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start">
              <ShieldCheckIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
              <div className="text-sm text-blue-700">
                <p className="font-medium mb-1">安全登录提示</p>
                <ul className="text-xs space-y-1 text-blue-600">
                  <li>• 验证码5分钟内有效</li>
                  <li>• 请勿向他人透露验证码</li>
                  <li>• 如未收到验证码，请检查手机号是否正确</li>
                </ul>
              </div>
            </div>
          </div>

          {/* 注册链接 */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600">
              还没有账户？{' '}
              <Link 
                to="/register" 
                className="font-medium text-green-600 hover:text-green-700 transition-colors duration-200"
              >
                立即注册，开始学习
              </Link>
            </p>
          </div>

          {/* 特色说明 */}
          <div className="mt-8 grid grid-cols-3 gap-4 text-center">
            <div className="text-center">
              <div className="text-2xl mb-1">📱</div>
              <div className="text-xs text-gray-600">手机登录</div>
            </div>
            <div className="text-center">
              <div className="text-2xl mb-1">⚡</div>
              <div className="text-xs text-gray-600">极速验证</div>
            </div>
            <div className="text-center">
              <div className="text-2xl mb-1">🔐</div>
              <div className="text-xs text-gray-600">安全保障</div>
            </div>
          </div>
        </div>

        {/* 底部链接 */}
        <div className="mt-6 text-center">
          <div className="flex items-center justify-center space-x-6 text-sm text-gray-500">
            <Link to="/privacy" className="hover:text-gray-700 transition-colors">隐私政策</Link>
            <span>|</span>
            <Link to="/terms" className="hover:text-gray-700 transition-colors">服务条款</Link>
            <span>|</span>
            <Link to="/help" className="hover:text-gray-700 transition-colors">帮助中心</Link>
          </div>
        </div>
      </div>
    </div>
  </div>
  )
}

export default PhoneLoginPage 